import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiX } from "react-icons/fi";
import RecordHandwriting from "./RecordHandwriting";
import QuestionAssetPreviewStrip, { getPreviewableQuestionAssets } from "../document-processing/QuestionAssetPreviewStrip";
import {
  getCachedMatplotlibPreview,
  getOrRenderMatplotlibPreview,
} from "../../shared/matplotlibPreviewCache";
import MathText from "./MathText";
import QuestionDifficultyBadge from "./QuestionDifficultyBadge";
import { answerStateStyles, answerStateAttributes, hasAnswerContent, getAnswerDisplayText } from "./QuestionAnswerState";
import { usePreviewActivity } from "../../shared/previewActivity";

export default function QuestionPreviewModal({
  question,
  onClose,
  meta = [],
  title = "題目預覽",
  previewMatplotlibCode,
  onAssetsRendered,
}) {
  const [renderedQuestion, setRenderedQuestion] = useState(question);
  // A modal left open on a hidden page must not keep the backend drawing.
  const pageActive = usePreviewActivity();
  const dialogRef = useRef(null);
  const [handwriting, setHandwriting] = useState(false);
  const [inkSession, setInkSession] = useState(false);
  const previewKey = question?.id || question?.prompt_md || question?.prompt_preview || "preview";
  const hasQuestion = Boolean(question);
  useEffect(() => { setHandwriting(false); setInkSession(false); }, [previewKey, hasQuestion]);

  useEffect(() => {
    if (!question) return undefined;

    const handleKeyDown = (event) => {
      if (inkSession || event.defaultPrevented || event.isComposing || event.keyCode === 229 ||
        dialogRef.current?.closest("[hidden]") ||
        (event.key === "Enter" && (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey))) return;
      const targetDialog = event.target?.closest?.('[role="dialog"], dialog');
      if (targetDialog && targetDialog !== dialogRef.current) return;
      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, question, inkSession]);

  useEffect(() => {
    let cancelled = false;
    if (!question) {
      setRenderedQuestion(null);
      return () => {
        cancelled = true;
      };
    }

    const assets = question.assets || [];
    const pythonAssetIndexes = assets
      .map((asset, index) => ({
        index,
        code: String(asset?.source_code || "").trim(),
        language: String(asset?.source_language || "").trim().toLowerCase(),
      }))
      .filter((asset) => asset.language === "python" && asset.code)
      .map((asset) => ({
        ...asset,
        cachedImage: getCachedMatplotlibPreview(asset.code),
      }));

    if (!pythonAssetIndexes.length || !previewMatplotlibCode || !pageActive) {
      setRenderedQuestion(question);
      return () => {
        cancelled = true;
      };
    }

    const pythonAssetByIndex = new Map(
      pythonAssetIndexes.map((asset) => [asset.index, asset]),
    );

    const initialAssets = assets.map((asset, index) => {
      const pythonAsset = pythonAssetByIndex.get(index);
      if (!pythonAsset) return asset;
      return pythonAsset.cachedImage
        ? {
            ...asset,
            preview_status: "success",
            preview_message: "",
            _preview_url: pythonAsset.cachedImage,
            _preview_source_code: pythonAsset.code,
          }
        : {
            ...asset,
            preview_status: "loading",
            preview_message: "後端圖片產生中",
          };
    });
    setRenderedQuestion({ ...question, assets: initialAssets });

    const pendingPythonAssets = pythonAssetIndexes.filter(
      (asset) => !asset.cachedImage,
    );
    if (!pendingPythonAssets.length) {
      onAssetsRendered?.(question, initialAssets);
      return () => {
        cancelled = true;
      };
    }

    // Closing the modal or switching question drops its queued renders.
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    (async () => {
      const results = await Promise.all(
        pendingPythonAssets.map(async (asset) => {
          try {
            return {
              ...asset,
              result: await getOrRenderMatplotlibPreview(
                asset.code,
                previewMatplotlibCode,
                { signal: controller?.signal },
              ),
            };
          } catch (error) {
            return {
              ...asset,
              result: {
                success: false,
                error: error?.message || "後端圖片產生失敗",
              },
            };
          }
        }),
      );
      if (cancelled) return;

      const resultsByIndex = new Map(results.map((item) => [item.index, item]));
      const nextAssets = assets.map((asset, index) => {
        const pythonAsset = pythonAssetByIndex.get(index);
        if (pythonAsset?.cachedImage) {
          return {
            ...asset,
            preview_status: "success",
            preview_message: "",
            _preview_url: pythonAsset.cachedImage,
            _preview_source_code: pythonAsset.code,
          };
        }
        const rendered = resultsByIndex.get(index);
        if (!rendered) return asset;
        if (rendered.result?.success && rendered.result.image) {
          return {
            ...asset,
            preview_status: "success",
            preview_message: "",
            _preview_url: rendered.result.image,
            _preview_source_code: rendered.code,
          };
        }
        return {
          ...asset,
          preview_status: "error",
          preview_message:
            rendered.result?.error || "後端圖片產生失敗，請檢查 Python 程式碼。",
          _preview_url: "",
          _preview_source_code: rendered.code,
        };
      });
      setRenderedQuestion({ ...question, assets: nextAssets });
      onAssetsRendered?.(question, nextAssets);
    })();

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [onAssetsRendered, previewMatplotlibCode, question, pageActive]);

  if (!question || !renderedQuestion) return null;

  const stem =
    renderedQuestion.prompt_md?.trim() ||
    renderedQuestion.prompt_preview?.trim() ||
    "";
  const optionLines = renderedQuestion.type === "choice"
    ? (renderedQuestion.choice_options || [])
        .map((option) => ({
          label: String(option.label || "").trim().replace(/[Ａ-Ｅ]/g, (char) =>
            String.fromCharCode(char.charCodeAt(0) - 0xfee0),
          ).toUpperCase(),
          text: String(option.text || "").trim(),
        }))
        .filter((option) => option.label && option.text)
        .map((option) => `(${option.label}) ${option.text}`)
    : [];
  const prompt = [stem, ...optionLines].filter(Boolean).join("\n");
  const visibleMeta = meta.filter(Boolean);
  // Older assets without a role belong to the question stem.
  const assets = (renderedQuestion.assets || []).map((asset) => ({
    ...asset,
    role: asset.role || "prompt",
  }));
  const hasSolution = Boolean(renderedQuestion.solution_md?.trim()) ||
    getPreviewableQuestionAssets(assets, "solution").length > 0;
  const assetRoleLabel = (role) => ({ prompt: "題目圖片", answer: "答案圖片", solution: "詳解圖片" }[role] || "圖片");

  return (
    <Overlay role="presentation" onClick={() => { if (!inkSession) onClose(); }}>
      <Dialog ref={dialogRef} role="dialog" aria-modal="true" aria-label="題目放大預覽">
        <Header onClick={(event) => event.stopPropagation()}>
          <HeaderActions>
            <HeaderButton type="button" aria-label={handwriting ? "結束手寫編輯" : "手寫編輯"} title={handwriting ? "結束手寫編輯" : "手寫編輯（關閉預覽後清除）"} aria-pressed={handwriting} onClick={() => { setInkSession(true); setHandwriting((value) => !value); }}><FiEdit2 /></HeaderButton>
            <HeaderButton type="button" aria-label="關閉題目預覽" title="關閉" onClick={onClose}><FiX /></HeaderButton>
          </HeaderActions>
          <Title>{title}</Title>
          <Meta>
            {visibleMeta.length > 0 && <span>{visibleMeta.join(" · ")}</span>}
            <QuestionDifficultyBadge difficulty={renderedQuestion.difficulty} />
          </Meta>
        </Header>

        <Body onClick={(event) => { if (inkSession) event.stopPropagation(); }}>
          <RecordHandwriting key={previewKey} storageKey={`question-preview:${previewKey}`} active={handwriting} persistent={false} responsive allowPdfExport={false} documentTitle={title}>
          <Paper>
          {renderedQuestion.status === "draft" && renderedQuestion.review_note && (
            <Section data-record-export-exclude="true" role="note"><Label>草稿疑義／待處理</Label><Text>{renderedQuestion.review_note}</Text></Section>
          )}
          <Section>
            <Label>題目</Label>
            <Prompt>
              <MathText source={prompt} fallback="尚無題目內容" />
            </Prompt>
            <QuestionAssetPreviewStrip assets={assets} role="prompt" defaultAlignment="right" getRoleLabel={assetRoleLabel} />
          </Section>

          <Section $tone="answer" $empty={!hasAnswerContent(renderedQuestion)}>
            <Label>答案</Label>
            <Text>
              <MathText source={getAnswerDisplayText(renderedQuestion)} fallback="" />
            </Text>
            <QuestionAssetPreviewStrip assets={assets} role="answer" defaultAlignment="right" getRoleLabel={assetRoleLabel} />
          </Section>

          {hasSolution && (
            <Section>
              <Label>詳解</Label>
              <Text>
                <MathText source={renderedQuestion.solution_md} fallback="" />
              </Text>
              <QuestionAssetPreviewStrip assets={assets} role="solution" defaultAlignment="right" getRoleLabel={assetRoleLabel} />
            </Section>
          )}
          </Paper>
          </RecordHandwriting>
        </Body>
      </Dialog>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(8px, 2vw, 20px);
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
  cursor: zoom-out;
`;

const Dialog = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 18px;
  width: min(960px, 100%);
  height: min(860px, calc(100dvh - 2 * clamp(8px, 2vw, 20px)));
  max-height: calc(100dvh - 2 * clamp(8px, 2vw, 20px));
  box-sizing: border-box;
  padding: clamp(14px, 2.2vw, 24px);
  border: 1px solid var(--app-border, #dedede);
  border-radius: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.38);
  cursor: zoom-out;

  @media (max-width: 640px) {
    gap: 12px;
    border-radius: 10px;
  }
`;

const Header = styled.header`
  position: relative;
  padding-right: 90px;
  display: grid;
  gap: 5px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(17px, 2vw, 22px);
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  color: var(--app-muted-text, #6b7280);
  font-size: clamp(11px, 1.3vw, 13px);
  font-weight: 800;
`;

const Body = styled.div`
  display: grid;
  align-content: start;
  gap: 14px;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (max-width: 640px) {
    gap: 10px;
  }
`;

const Section = styled.section.attrs((props) =>
  props.$tone === "answer" ? answerStateAttributes(props) : {},
)`
  display: grid;
  gap: 10px;
  padding: clamp(11px, 1.8vw, 18px);
  border: 1px solid
    ${({ $tone }) =>
      $tone === "answer" ? "rgba(22, 163, 74, 0.3)" : "var(--app-border, #e5e7eb)"};
  border-radius: 10px;
  background: ${({ $tone }) =>
    $tone === "answer" ? "rgba(22, 163, 74, 0.06)" : "var(--app-input-bg, #fff)"};
  ${({ $tone }) => $tone === "answer" && answerStateStyles}
`;

const Label = styled.div`
  color: var(--answer-label-color, var(--app-muted-text, #6b7280));
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
`;

const Prompt = styled.div`
  font-size: clamp(15px, 1.7vw, 19px);
  font-weight: 700;
  line-height: 1.65;
  white-space: pre-wrap;
`;

const Text = styled.div`
  font-size: clamp(14px, 1.5vw, 17px);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const HeaderActions = styled.div`
  position: absolute; right: 0; top: 0; display: flex; gap: 8px;
`;
const HeaderButton = styled.button`
  display: grid; place-items: center; width: 36px; height: 36px;
  border: 1px solid var(--app-border, #dedede); border-radius: 10px;
  background: var(--app-input-bg, #fff); color: var(--app-surface-text, #202124); cursor: pointer;
  font-size: 18px;
  &[aria-pressed="true"] { background: #2463eb; color: #fff; border-color: #2463eb; }
  &:hover { box-shadow: 0 0 0 2px rgba(36,99,235,.18); }
`;
const Paper = styled.div`display: grid; align-content: start; gap: 14px; min-height: 520px;`;
